import {
  Box,
  Button,
  HStack,
  Stack,
  useColorMode,
  VStack,
  Text,
} from '@chakra-ui/react'
import React from 'react'
import { toast } from 'react-hot-toast'
import { FormInputWithMode } from '../../../components/Forms/FormInputWithMode'
import { FormLabelWithMode } from '../../../components/Forms/FormLabelWithMode'
import { trpc } from '../../../utils/trpc'
import AdminUserInfoAndFlagBox from '../../platformAdmin/components/AdminUserInfoAndFlagBox'
import { UseOnChainRaffleType } from '../../techRaffles/hooks/hookTypes'
import { raffleType } from '../../techRaffles/types'
import { RaffleOnChainInfoBox } from './RaffleOnChainInfoBox'

type PlatformAdminRaffleBoxProps = {
  onChainRaffleRes: UseOnChainRaffleType
  raffle: raffleType
}

export const PlatformAdminRaffleInfoBox: React.FC<
  PlatformAdminRaffleBoxProps
> = ({ raffle, onChainRaffleRes }) => {
  const { colorMode } = useColorMode()
  const isDarkMode = colorMode === 'dark'

  const [multiplier, setMultiplier] = React.useState<number | string>(
    raffle.pointsMultiplier
  )

  const adminRaffleViewRes = trpc.useQuery(
    ['raffle.getAdminRaffleView', { raffleId: raffle.id }],
    { enabled: !!raffle.id }
  )

  React.useEffect(() => {
    setMultiplier(raffle.pointsMultiplier)
  }, [raffle.pointsMultiplier])

  const updatePointsMultiplierMut = trpc.useMutation(
    'raffle.updatePointsMultiplier',
    {
      onSuccess: () => {
        toast.success('Points multiplier has been updated')
      },
      onError: (e) => {
        toast.error(`Could not update points mut raffle: ${e.message}`)
      },
    }
  )

  const haltDrawMut = trpc.useMutation('raffle.haltDraw', {
    onSuccess: () => {
      toast.success('Automatic draw has been halted')
    },
    onError: (e) => {
      toast.error(`Could not halt raffle: ${e.message}`)
    },
  })
  const continueDrawMut = trpc.useMutation('raffle.continueDraw', {
    onSuccess: () => {
      toast.success('Automatic draw has been continue')
    },
    onError: (e) => {
      toast.error(`Could not continue draw for raffle: ${e.message}`)
    },
  })
  const forceDrawMut = trpc.useMutation('raffle.forceDraw', {
    onSuccess: () => {
      toast.success('Automatic draw has been forced')
    },
    onError: (e) => {
      toast.error(`Could not force draw for raffle: ${e.message}`)
    },
  })
  const verifyOnChainBuyersMut = trpc.useMutation(
    'raffle.verifyOnChainBuyers',
    {
      onSuccess: () => {
        toast.success('Draw is checking raffle buyers on-chain before draw')
      },
      onError: (e) => {
        toast.error(`Could not change: ${e.message}`)
      },
    }
  )
  const forceInFeaturedMut = trpc.useMutation(
    'raffle.toggleForceInFeaturedSection',
    {
      onSuccess: () => {
        toast.success('Raffle will now be shown in featured category')
      },
      onError: (e) => {
        toast.error(`Failed: ${e.message}`)
      },
    }
  )
  const setTrendingMut = trpc.useMutation('raffle.toggleSetAsTrending', {
    onSuccess: () => {
      toast.success('Raffle will now be shown as trending')
    },
    onError: (e) => {
      toast.error(`Failed: ${e.message}`)
    },
  })

  const toggleHideFromTrendingMut = trpc.useMutation(
    'raffle.toggleHideFromTrending',
    {
      onSuccess: () => {
        toast.success('Raffle wont be shown in trending anymore')
      },
      onError: (e) => {
        toast.error(`Failed: ${e.message}`)
      },
    }
  )

  const toggleHideFromFeaturedMut = trpc.useMutation(
    'raffle.toggleHideFromFeatured',
    {
      onSuccess: () => {
        toast.success('Raffle wont be shown in featured anymore')
      },
      onError: (e) => {
        toast.error(`Failed: ${e.message}`)
      },
    }
  )

  return (
    <Box mt='1.5rem'>
      <RaffleOnChainInfoBox
        raffle={raffle}
        onChainRaffleRes={onChainRaffleRes}
      ></RaffleOnChainInfoBox>
      {raffle.status === 'SCHEDULED' && (
        <VStack
          mt='1rem'
          bg={isDarkMode ? 'cardBlack' : '#F9F9F9'}
          borderRadius='1.875rem'
          paddingY='1.5rem'
          paddingX='2.5rem'
          spacing={5}
        >
          <Text fontSize='1.1rem' fontWeight={700}>
            Draw control
          </Text>
          <Stack spacing={3} direction={['column', 'column', 'row']} w='100%'>
            <Button
              isLoading={haltDrawMut.isLoading}
              colorScheme='red'
              fontSize='1rem'
              onClick={() => {
                haltDrawMut.mutateAsync({
                  raffleId: raffle.id,
                })
              }}
            >
              Halt raffle draw
            </Button>
            <Button
              isLoading={continueDrawMut.isLoading}
              colorScheme='teal'
              fontSize='1rem'
              onClick={() => {
                continueDrawMut.mutateAsync({
                  raffleId: raffle.id,
                })
              }}
            >
              Continue draw
            </Button>
            <Button
              isLoading={forceDrawMut.isLoading}
              colorScheme='orange'
              fontSize='1rem'
              onClick={() => {
                forceDrawMut.mutateAsync({
                  raffleId: raffle.id,
                })
              }}
            >
              Force draw
            </Button>
            <Button
              isLoading={verifyOnChainBuyersMut.isLoading}
              colorScheme='green'
              fontSize='1rem'
              onClick={() => {
                verifyOnChainBuyersMut.mutateAsync({
                  raffleId: raffle.id,
                })
              }}
            >
              Verify ticket buyers on-chain
            </Button>
          </Stack>
          <Text fontSize='1.1rem' fontWeight={700}>
            Viewability control
          </Text>

          {adminRaffleViewRes.isLoading ? (
            <Text mt='1rem'>Loading latest state...</Text>
          ) : (
            <>
              <Text textAlign='left'>
                <b>Current state</b>
                <br></br> Set to show in featured:{' '}
                {adminRaffleViewRes.data?.isInFeaturedSection ? 'Yes' : 'No'}
                <br></br> Set to show in trending:{' '}
                {adminRaffleViewRes.data?.setTrending ? 'Yes' : 'No'}
                <br></br> Hide from featured:{' '}
                {adminRaffleViewRes.data?.hideFromFeatured ? 'Yes' : 'No'} (not
                overwritten by above)
                <br></br> Hide from trending:{' '}
                {adminRaffleViewRes.data?.hideFromTrending ? 'Yes' : 'No'} (not
                overwritten by above)
              </Text>
              <Stack
                spacing={3}
                direction={['column', 'column', 'row']}
                w='100%'
              >
                <Button
                  isLoading={forceInFeaturedMut.isLoading}
                  variant={isDarkMode ? 'primaryDark' : 'primary'}
                  onClick={async () => {
                    await forceInFeaturedMut.mutateAsync({
                      raffleId: raffle.id,
                      isInFeaturedSection:
                        !adminRaffleViewRes.data?.isInFeaturedSection,
                    })
                    await adminRaffleViewRes.refetch()
                  }}
                  fontSize='1rem'
                >
                  Force in featured category
                </Button>

                <Button
                  isLoading={setTrendingMut.isLoading}
                  variant='secondaryOnGrey'
                  onClick={async () => {
                    await setTrendingMut.mutateAsync({
                      raffleId: raffle.id,
                      setTrending: !adminRaffleViewRes.data?.setTrending,
                    })
                    await adminRaffleViewRes.refetch()
                  }}
                  fontSize='1rem'
                >
                  Set trending raffle
                </Button>

                <Button
                  isLoading={toggleHideFromTrendingMut.isLoading}
                  variant='secondaryOnGrey'
                  onClick={async () => {
                    await toggleHideFromTrendingMut.mutateAsync({
                      raffleId: raffle.id,
                      hideFromTrending:
                        !adminRaffleViewRes.data?.hideFromTrending,
                    })
                    await adminRaffleViewRes.refetch()
                  }}
                  fontSize='1rem'
                >
                  {adminRaffleViewRes.data?.hideFromTrending
                    ? 'Unhide'
                    : 'Hide'}{' '}
                  from trending
                </Button>

                <Button
                  isLoading={toggleHideFromFeaturedMut.isLoading}
                  variant='secondaryOnGrey'
                  onClick={async () => {
                    await toggleHideFromFeaturedMut.mutateAsync({
                      raffleId: raffle.id,
                      hideFromFeatured:
                        !adminRaffleViewRes.data?.hideFromFeatured,
                    })
                    await adminRaffleViewRes.refetch()
                  }}
                  fontSize='1rem'
                >
                  {adminRaffleViewRes.data?.hideFromFeatured
                    ? 'Unhide'
                    : 'Hide'}{' '}
                  from featured
                </Button>
              </Stack>
            </>
          )}
          <Text fontSize='1.1rem' fontWeight={700}>
            Points multiplier
          </Text>
          <Stack direction={['column', 'row']}>
            <FormInputWithMode
              width='100px'
              type='number'
              value={multiplier}
              onChange={(e) => setMultiplier(e.target.value)}
            ></FormInputWithMode>
            <Button
              isLoading={updatePointsMultiplierMut.isLoading}
              variant='secondaryOnGrey'
              onClick={() => {
                if (isNaN(Number(multiplier))) {
                  toast.error('Multiplier must be a number')
                  return
                }
                updatePointsMultiplierMut.mutateAsync({
                  raffleId: raffle.id,
                  pointsMultiplier: Number(multiplier),
                })
              }}
              fontSize='1rem'
              minWidth='220px'
              colorScheme='green'
            >
              Update multiplier
            </Button>
          </Stack>
        </VStack>
      )}

      {adminRaffleViewRes.data && (
        <VStack
          mt='1rem'
          bg={isDarkMode ? 'cardBlack' : '#F9F9F9'}
          borderRadius='1.875rem'
          paddingY='1.5rem'
          paddingX='2.5rem'
          spacing={5}
        >
          {adminRaffleViewRes.data.flagReason && (
            <>
              <Text fontSize='1.1rem' fontWeight={700}>
                Raffle Flag Reason
              </Text>
              <Text maxWidth='100%' whiteSpace={'normal'}>{adminRaffleViewRes.data.flagReason}</Text>
            </>
          )}
          {adminRaffleViewRes.data.creatorUser && (
            <AdminUserInfoAndFlagBox
              user={adminRaffleViewRes.data.creatorUser}
            ></AdminUserInfoAndFlagBox>
          )}
        </VStack>
      )}
    </Box>
  )
}

export default PlatformAdminRaffleInfoBox
